import { useCallback, useEffect, useRef, useState } from 'react';
import { oneMinute } from 'utils/time';

export const DEFAULT_AD_FREQUENCY = oneMinute;

const useAdPlayback = ({
  cameraId,
  multiCamViewActive,
  disableAds,
  hasAdSupportedCamPermissions,
  isNonPremiumPlaylist,
  pause,
  play,
  playerPlaying,
}: {
  cameraId?: string;
  multiCamViewActive?: boolean;
  disableAds: boolean;
  hasAdSupportedCamPermissions: boolean;
  isNonPremiumPlaylist: boolean;
  pause: () => void;
  play: () => void;
  playerPlaying: boolean;
}) => {
  const playAdRef = useRef(() => {});
  const [adRunning, setAdRunning] = useState<boolean | undefined>(undefined);
  const hasPlayedPreroll = useRef(false);

  const adRunningRef = useRef(adRunning);

  useEffect(() => {
    if (!adRunningRef.current) {
      hasPlayedPreroll.current = false;
    }
  }, [cameraId, multiCamViewActive]);

  useEffect(() => {
    adRunningRef.current = adRunning;
    if (adRunning) {
      pause();
    } else if (adRunning === false) {
      play();
    }
  }, [adRunning, pause, play]);

  const onPlayedDurationUpdate = useCallback(
    (playedMs: number, resetPlayedMs: () => void) => {
      if (disableAds || (!isNonPremiumPlaylist && !hasAdSupportedCamPermissions)) return;
      if (!hasPlayedPreroll.current) {
        hasPlayedPreroll.current = true;
        playAdRef.current();
        resetPlayedMs();
      } else if (
        !hasAdSupportedCamPermissions &&
        !adRunningRef.current &&
        playedMs > DEFAULT_AD_FREQUENCY
      ) {
        playAdRef.current();
        resetPlayedMs();
      }
    },
    [disableAds, isNonPremiumPlaylist, hasAdSupportedCamPermissions],
  );

  useEffect(() => {
    if (disableAds) return;
    if (playerPlaying && !hasPlayedPreroll.current && playAdRef.current) {
      hasPlayedPreroll.current = true;
      playAdRef.current();
    }
  }, [multiCamViewActive, playerPlaying, disableAds, cameraId, playAdRef]);

  const setPlayAd = useCallback((playAd: () => void) => {
    playAdRef.current = playAd;
  }, []);

  return { setPlayAd, adRunning, setAdRunning, onPlayedDurationUpdate };
};

export default useAdPlayback;
